import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, FileDown, Upload, CheckCircle2, Paperclip, MessageSquare, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api/cee/depot`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const Row = ({ label, value }) => (value === null || value === undefined || value === '' ? null : (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{label}</span><span className="font-semibold text-slate-900 text-right">{value}</span>
  </div>
));

function Summary({ s, pdfUrl }) {
  const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString('fr-FR')} €` : null);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="depot-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-[#004AAD]" />
          <h2 className="font-bold text-slate-900">Compte rendu de votre dossier</h2></div>
        <a href={`${BACKEND}${pdfUrl}`} target="_blank" rel="noreferrer" data-testid="depot-rapport-pdf">
          <Button variant="outline" size="sm"><FileDown className="w-4 h-4 mr-1.5" />Télécharger le PDF</Button>
        </a>
      </div>
      {s.eligibilite?.length > 0 && (
        <div className="mb-3 space-y-1">
          {s.eligibilite.map((e) => <div key={e} className="text-sm bg-emerald-50 text-emerald-800 rounded-md px-3 py-1.5">{e}</div>)}
        </div>
      )}
      <Row label="Adresse" value={s.adresse} />
      <Row label="Âge" value={s.age ? `${s.age} ans` : null} />
      <Row label="Personnes dans le ménage" value={s.menage_personnes} />
      <Row label="Revenu fiscal de référence" value={eur(s.revenu_fiscal_reference)} />
      <Row label="Parts fiscales" value={s.nombre_parts} />
      <Row label="Catégorie Anah" value={s.categorie_anah ? `${s.categorie_anah} (${s.zone})` : null} />
      <Row label="Compte Anah actif" value={s.compte_anah} />
      <Row label="Identité numérique validée" value={s.identite_numerique} />
      {!s.age && !s.menage_personnes && !s.revenu_fiscal_reference && !s.eligibilite?.length && (
        <p className="text-xs text-slate-500 italic mt-3" data-testid="depot-summary-pending">
          Le compte rendu sera complété par notre équipe au fur et à mesure de l'étude de votre dossier.
        </p>
      )}
    </div>
  );
}

function DocItem({ token, item, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('Fichier trop volumineux (15 Mo max)'); return; }
    setBusy(true);
    const fd = new FormData(); fd.append('item_key', item.key); fd.append('file', file);
    try {
      await axios.post(`${API}/${token}/upload`, fd);
      toast.success('Document envoyé'); onUploaded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const done = item.files.length > 0;
  return (
    <div className={`border rounded-lg p-4 transition-colors ${done ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`} data-testid={`depot-item-${item.key}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <Paperclip className="w-5 h-5 text-slate-400 shrink-0" />}
          <span className="font-medium text-slate-900 text-sm">{item.label}</span>
        </div>
        <input ref={ref} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.doc,.docx"
               onChange={(e) => upload(e.target.files?.[0])} data-testid={`depot-input-${item.key}`} />
        <Button size="sm" variant={done ? 'outline' : 'default'} disabled={busy} onClick={() => ref.current?.click()}
                className={`w-full sm:w-auto ${done ? '' : 'bg-[#004AAD] hover:bg-[#003580]'}`} data-testid={`depot-upload-btn-${item.key}`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
          {done ? 'Ajouter un fichier' : 'Déposer'}
        </Button>
      </div>
      {item.files.map((f) => (
        <div key={f.file_id} className="text-xs text-slate-600 mt-2 ml-7">{f.filename} · envoyé le {fmt(f.uploaded_at)}</div>
      ))}
    </div>
  );
}

function Comments({ token, comments, onSent }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/${token}/comment`, { text }); setText(''); toast.success('Commentaire envoyé'); onSent(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="depot-comments">
      <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#004AAD]" />
        <h2 className="font-bold text-slate-900">Un commentaire ?</h2></div>
      {comments.map((c, i) => (
        <div key={i} className="text-sm bg-slate-50 rounded-lg p-3 whitespace-pre-line">{c.text}
          <div className="text-[11px] text-slate-400 mt-1">{fmt(c.at)}</div></div>
      ))}
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Une précision, une question, une correction sur le compte rendu…" data-testid="depot-comment-input" />
      <div className="flex justify-end">
        <Button onClick={send} disabled={busy || !text.trim()} className="bg-[#004AAD] hover:bg-[#003580]" data-testid="depot-comment-send-btn">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Envoyer
        </Button>
      </div>
    </div>
  );
}

export default function DepotDocuments() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const load = async () => {
    try { const r = await axios.get(`${API}/${token}`); setState({ loading: false, data: r.data, error: null }); }
    catch (e) { setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Lien invalide' }); }
  };
  useEffect(() => { document.title = 'Dépôt de documents — Reno Resolve'; load(); /* eslint-disable-next-line */ }, [token]);

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#004AAD]" /></div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white p-8 rounded-2xl border border-slate-200 max-w-md text-center" data-testid="depot-error">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-slate-900 mb-1">Lien indisponible</h2>
        <p className="text-slate-500 text-sm">{state.error}</p>
      </div>
    </div>
  );
  const d = state.data;
  const r = d.request;
  const received = r.items.filter((i) => i.files.length).length;
  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="depot-page">
      <div className="bg-[#004AAD] text-white">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <img src="/logo.png" alt="Reno Resolve" className="h-9 w-auto bg-white/95 rounded-lg px-3 py-1.5 mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Bonjour {d.beneficiaire}</h1>
          <p className="text-blue-100 text-sm mt-1">Dossier MaPrimeAdapt' {d.dossier_id} · lien valable jusqu'au {fmt(r.expires_at)}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {r.message && <div className="bg-blue-50 border-l-4 border-[#004AAD] rounded p-4 text-sm text-blue-900 whitespace-pre-line">{r.message}</div>}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-slate-900">Documents à transmettre</h2>
            <span className="text-xs text-slate-500" data-testid="depot-progress">{received}/{r.items.length} reçus</span>
          </div>
          <p className="text-xs text-slate-500">PDF, photo (JPG, PNG, HEIC) ou Word · 15 Mo max par fichier.</p>
          {r.items.map((it) => <DocItem key={it.key} token={token} item={it} onUploaded={load} />)}
        </div>
        <Summary s={d.summary} pdfUrl={d.rapport_pdf_url} />
        <Comments token={token} comments={r.comments} onSent={load} />
        <p className="text-[11px] text-center text-slate-400">Vos documents sont transmis de façon sécurisée et utilisés uniquement pour votre dossier (RGPD · Reno Resolve).</p>
      </div>
    </div>
  );
}
